import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = () => getApiBaseUrl();

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 2000,
    background: 'rgba(0,0,0,0.55)', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
  },
  modal: {
    background: '#fff', borderRadius: 16, width: '100%',
    maxWidth: 520, maxHeight: '90vh', overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
  },
  header: {
    padding: '20px 24px 16px', borderBottom: '1px solid #f0f0f0',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  title: { margin: 0, fontSize: 17, fontWeight: 700, color: '#1a1a2e' },
  desc: { margin: '4px 0 0', fontSize: 12, color: '#888', lineHeight: 1.5 },
  closeBtn: {
    background: 'none', border: 'none', fontSize: 20,
    cursor: 'pointer', color: '#999', padding: '0 4px',
  },
  body: { padding: '20px 24px', overflowY: 'auto', flex: 1 },
  addRow: { display: 'flex', gap: 8, marginBottom: 20 },
  input: {
    flex: 1, padding: '9px 12px', border: '1.5px solid #e0e0e0',
    borderRadius: 8, fontSize: 14, outline: 'none',
  },
  addBtn: {
    padding: '9px 16px', borderRadius: 8, border: 'none',
    background: 'linear-gradient(135deg,#2c5f8a,#1a3d5c)',
    color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  emptyMsg: {
    textAlign: 'center', padding: '32px 0', color: '#aaa', fontSize: 14,
  },
  notice: {
    background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8,
    padding: '10px 14px', fontSize: 12, color: '#795548',
    marginBottom: 16, lineHeight: 1.6,
  },
  groupItem: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 14px', borderRadius: 10,
    border: '1px solid #eef0f4', marginBottom: 8,
    background: '#fafbfc',
  },
  groupEmail: { fontSize: 14, fontWeight: 600, color: '#1a1a2e' },
  groupLabel: { fontSize: 12, color: '#888', marginTop: 2 },
  deleteBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#e57373', fontSize: 18, padding: '2px 6px',
    borderRadius: 6, lineHeight: 1,
  },
  errorMsg: { color: '#e53935', fontSize: 13, marginTop: 8 },
};

export default function AutoApproveGroupsModal({ open, onClose }) {
  const { user } = useAuth();
  const token = (user && user.token) ? String(user.token) : '';
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const authHeader = useCallback(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token],
  );

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE()}/api/auth/allowed_groups`, { headers: authHeader() });
      const data = await res.json();
      if (data.ok) setGroups(data.groups || []);
      else setError(data.detail || '불러오기 실패');
    } catch {
      setError('서버 연결 오류');
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    if (open) {
      fetchGroups();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, fetchGroups]);

  const handleAdd = async () => {
    const ge = newEmail.trim().toLowerCase();
    if (!ge || !ge.includes('@')) {
      setError('유효한 그룹 이메일을 입력해 주세요');
      return;
    }
    setError('');
    try {
      const res = await fetch(`${API_BASE()}/api/auth/allowed_groups`, {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({ group_email: ge, label: newLabel.trim() || ge }),
      });
      const data = await res.json();
      if (data.ok) {
        setNewEmail('');
        setNewLabel('');
        fetchGroups();
      } else {
        setError(data.detail || '추가 실패');
      }
    } catch {
      setError('서버 연결 오류');
    }
  };

  const handleDelete = async (ge) => {
    if (!window.confirm(`"${ge}" 그룹을 허용 목록에서 제거하시겠습니까?`)) return;
    try {
      const res = await fetch(
        `${API_BASE()}/api/auth/allowed_groups/${encodeURIComponent(ge)}`,
        { method: 'DELETE', headers: authHeader() },
      );
      const data = await res.json();
      if (data.ok) fetchGroups();
      else setError(data.detail || '삭제 실패');
    } catch {
      setError('서버 연결 오류');
    }
  };

  if (!open) return null;

  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>🔒 로그인 허용 그룹 관리</h2>
            <p style={styles.desc}>
              등록된 Google Workspace 그룹에 속한 멤버만 이 시스템에 로그인·가입 신청할 수 있습니다.<br />
              그룹이 하나도 없으면 워크스페이스 계정이면 누구나 진입 가능합니다.
            </p>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={styles.body}>
          <div style={styles.notice}>
            ⚠️ 그룹을 등록하면 해당 그룹 멤버만 로그인 및 가입 신청이 가능합니다.<br />
            예: <strong>workspace@mrt.co.kr</strong> 를 등록하면 그 그룹에 속한 사람만 진입 가능합니다.
          </div>

          <div style={styles.addRow}>
            <input
              ref={inputRef}
              style={styles.input}
              placeholder="그룹 이메일 (예: workspace@mrt.co.kr)"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
            <button style={styles.addBtn} onClick={handleAdd}>추가</button>
          </div>
          <div style={{ marginBottom: 14 }}>
            <input
              style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
              placeholder="표시 이름 (선택, 예: 전 직원)"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
          </div>
          {error && <p style={styles.errorMsg}>{error}</p>}

          {loading ? (
            <p style={styles.emptyMsg}>불러오는 중…</p>
          ) : groups.length === 0 ? (
            <p style={styles.emptyMsg}>
              등록된 허용 그룹이 없습니다.<br />
              <small style={{ color: '#bbb' }}>현재 워크스페이스 계정이면 누구나 진입할 수 있습니다.</small>
            </p>
          ) : (
            groups.map((g) => (
              <div key={g.group_email} style={styles.groupItem}>
                <div>
                  <div style={styles.groupEmail}>{g.group_email}</div>
                  {g.label && g.label !== g.group_email && (
                    <div style={styles.groupLabel}>{g.label}</div>
                  )}
                </div>
                <button style={styles.deleteBtn} onClick={() => handleDelete(g.group_email)} title="제거">✕</button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
